/* eslint-disable  @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Table } from "reactstrap";
import {
  AlertComponent,
  BreadcrumbComponent,
} from "../../../common/components";
import { useSetLoading } from "../../../common/hooks/appearance";
import arrowWithBox from "../../../assets/icons/arrowWithBox.svg";
import RolesConfirmationPopup from "./roles-confirmation-popup";
import RolesCreationPopup from "./create-role/create-role";
import "./user-management-role.scss";
import { getTenantsList } from "../../../api/tenets";
import CompanySelect from "../user-management-list/components/company-select/company-select";
import {
  createNewUserRole,
  getUserRolePermissions,
  manageNewRole,
  updateRolePermissions,
} from "../../../api/user-management";
import backgroundIcon from "../../../assets/icons/user-role-company-search-icon.svg";
import { AlertMessage } from "../../tenant/tenant-details/tenant-details-types";
import {
  applyScopeChangesToRoles,
  formatLastUpdatedDate,
  preprocessPermissionsData,
  setAllScopesToFalse,
} from "./user-management-role-utils";
import { useLogin } from "../../../common/hooks/useUser";
import RoleDetails from "./role-details/role-details";
import _ from "lodash";
import { useResourcePermissions } from "../../../common/hooks/resource-permissions";
import MODULES from "../../../constants/modules";
import icons from "../../../assets/icons/icons";

const UserManagementRole: React.FC = () => {
  const [rolesData, setRoles] = useState<any[]>([]);
  const [rolePermissions, setRolePermissions] = useState<any[]>([]);
  const [permissions, setPermissions] = useState<any[]>([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isCreateRolePopupOpen, setIsCreateRolePopupOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<"AFP" | "Partner">("AFP");
  const [companyNames, setCompanyNames] = useState<any[]>([]);
  const [company, setCompany] = useState<any>(null);
  const [lastUpdated, setLastUpdated] = useState<string>("");
  const [createNewRole, setCreateNewRole] = useState<any>({
    oldPermissions: [],
    newPermissions: [],
    isCreate: false,
  });
  const [alertMessage, setAlertMessage] = useState<AlertMessage>({
    open: false,
    message: "",
    type: undefined,
  });
  const [modifiedPermissions, setModifiedPermissions] = useState<
    Record<
      string,
      Record<
        string,
        Record<
          string,
          {
            read: boolean;
            write: boolean;
            delete: boolean;
          }
        >
      >
    >
  >({});
  const [expandedModules, setExpandedModules] = useState<
    Record<string, boolean>
  >({});
  const [isRoleDetailsOpen, setIsRoleDetailsOpen] = useState<boolean>(false);
  const [selectedRoleName, setSelectedRoleName] = useState("");
  const [selectedCompany, setSelectedCompany] = useState<any>(null);

  const { canWrite } = useResourcePermissions(
    MODULES.USER_ROLES_AND_PERMISSIONS
  );

  const toggleModule = (moduleName: string) => {
    setExpandedModules((prev) => ({
      ...prev,
      [moduleName]: !prev[moduleName],
    }));
  };

  const crumbMapping = [
    { label: "User Management", link: "#", handleClick: () => {} },
    { label: "User Roles & Permissions", link: "#" },
  ];

  const columns = useMemo(
    () => [
      { label: "Super Admin", count: "43 User" },
      { label: "Admin", count: "32 User" },
      { label: "Partner", count: "55 User" },
      { label: "Agent", count: "44 User" },
      { label: "customer", count: "50 User" },
    ],
    []
  );

  const tenantDetails = useLogin();
  const isSuperParter = useMemo(() => {
    return tenantDetails?.tenantDetails?.tenantType === "SuperTenant";
  }, [tenantDetails?.tenantDetails?.tenantType]);

  const currentTenantId = tenantDetails?.tenantDetails?.id;

  const fetchRoles = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        const response = await getUserRolePermissions(Number(id));
        setRoles(response.data.data.roles);
        setPermissions(response.data.data.permissions);
        setLastUpdated(response.data.data.lastUpdatedAt);
      } catch {
        setRoles([]);
        setRolePermissions([]);
      } finally {
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      }
    },
    [columns]
  );

  const getTenentList = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getTenantsList({ isActive: true, dropDown: true });
      const items = response?.data?.data || [];
      const uniqueCompanies: { id: number; company_name: string }[] = [];

      for (const item of items) {
        if (item?.company_name) {
          uniqueCompanies.push({
            id: item.id,
            company_name: item.company_name,
          });
        }
      }
      setCompanyNames(uniqueCompanies);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperParter) {
      getTenentList();
    }
  }, [getTenentList]);

  useEffect(() => {
    if (activeTab === "Partner") {
      if (company) {
        setSelectedCompany(company);
        fetchRoles(company);
      }
    } else {
      setSelectedCompany(currentTenantId);
      fetchRoles(currentTenantId);
    }
  }, [activeTab, company, fetchRoles, currentTenantId]);

  useEffect(() => {
    const updated = permissions.map((item) => ({
      ...item,
      isShowData: false,
    }));
    setRolePermissions(updated);
  }, [permissions]);

  const handleCheckboxChange = (
    role: string,
    module: string,
    subresource: string,
    scope?: "read" | "write" | "delete"
  ) => {
    setModifiedPermissions((prevModified) => {
      const updated = { ...prevModified };

      if (!updated[role]) updated[role] = {};
      if (!updated[role][module]) updated[role][module] = {};
      if (["Import", "Export"].includes(subresource)) {
        const isChecked =
          (updated[role][module][subresource]?.read &&
            updated[role][module][subresource]?.write &&
            updated[role][module][subresource]?.delete) ??
          (permissionsData?.[role]?.[module]?.[subresource]?.read &&
            permissionsData?.[role]?.[module]?.[subresource]?.write &&
            permissionsData?.[role]?.[module]?.[subresource]?.delete) ??
          false;
        updated[role][module][subresource] = {
          read: !isChecked,
          write: !isChecked,
          delete: !isChecked,
        };
      } else if (!updated[role][module][subresource]) {
        const existing = permissionsData?.[role]?.[module]?.[subresource] || {
          read: false,
          write: false,
          delete: false,
        };
        updated[role][module][subresource] = { ...existing };
      }

      if (scope) {
        updated[role][module][subresource][scope] =
          !updated[role][module][subresource][scope];
      }

      return updated;
    });
  };

  const handleSaveChanges = async (roleName?: string) => {
    try {
      setLoading(true);
      const payload: any = { roleName };
      if (company) {
        payload.tenantId = company;
      }
      const res = await manageNewRole(payload);
      const newPermissions = setAllScopesToFalse(permissions[0]);
      setModifiedPermissions({});
      setCreateNewRole({
        oldPermissions: permissions,
        newPermissions: { ...newPermissions, userCount: 0, role: roleName },
        isCreate: true,
      });

      setPermissions([{ ...newPermissions, userCount: 0, role: roleName }]);
      setLoading(false);
      setIsCreateRolePopupOpen(false);
    } catch (error: any) {
      console.error("Error saving permissions:", error);
      setAlertMessage({
        open: true,
        message:
          error?.response?.data?.message || "Failed to update permissions",
        type: "error",
      });
      setIsPopupOpen(false);
      setLoading(false);
    }
  };

  const handleUpdatePermissions = async (payload: any) => {
    try {
      setLoading(true);
      const params: any = {};

      if (!isSuperParter || activeTab === "Partner") {
        params.tenantId = company;
      }
      const res = await updateRolePermissions(payload, params);
      fetchRoles(company || currentTenantId);
      setModifiedPermissions({});
      setAlertMessage({
        open: true,
        message:
          res?.data?.data?.message || "Updated role permissions successfully",
        type: "success",
      });
      setLoading(false);
    } catch (error) {
      console.error("Error saving permissions:", error);
      setAlertMessage({
        open: true,
        message: "Failed to update permissions",
        type: "error",
      });
      setIsPopupOpen(false);
      setLoading(false);
    }
  };

  const handleCreateRole = async (roleName: string) => {
    try {
      setLoading(true);
      const result: any = applyScopeChangesToRoles(
        permissions,
        modifiedPermissions
      );
      delete result[0].userCount;
      delete result[0].id;
      result[0].roleName = result[0].role;
      delete result[0].role;
      const payload: any = result[0];
      const params: any = {};
      if (!isSuperParter || activeTab === "Partner") {
        params.tenantId = company || currentTenantId;
      }

      const res = await createNewUserRole(payload, params);
      setRoles(res.data.data.data.roles);
      setPermissions(res.data.data.data.permissions);
      setLastUpdated(res.data.data.data.lastUpdatedAt);
      setModifiedPermissions({});
      setCreateNewRole({
        isCreate: false,
        newPermissions: [],
        oldPermissions: [],
      });

      setAlertMessage({
        open: true,
        message: res?.data?.data?.message || "Created role successfully",
        type: "success",
      });
      setLoading(false);
    } catch (error) {
      console.error("Error saving permissions:", error);
      setAlertMessage({
        open: true,
        message: "Failed to update permissions",
        type: "error",
      });
      setIsPopupOpen(false);
      setLoading(false);
    }
  };

  const handleRoleClick = (roleName: string) => {
    setSelectedRoleName(roleName);
    setIsRoleDetailsOpen(true);
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  const processedData = preprocessPermissionsData(rolePermissions);
  const { roles, roleUserCounts, modules, permissionsData } = processedData;
  const renderPermisssionComponent = () => {
    return (
      <>
        <div className="roles-table-container">
          <Table
            className="roles-table"
            hover
            bordered
            borderless={true}
            responsive
          >
            <thead>
              <tr className="sticky-cl">
                <th className={"sticky-cl"}>
                  <div className="roles-table-container-header">
                    <div className="roles-table-container-header-text">
                      {"Modules & Sub Modules"}
                    </div>
                  </div>
                </th>
                {roles.map((role) => (
                  <th key={role}>
                    <div className="roles-table-container-header">
                      <div className="roles-table-container-header-text">
                        {role}
                      </div>
                      <div className="roles-table-container-header-count-icons">
                        <div
                          className="roles-table-container-header-count"
                          onClick={() => handleRoleClick(role)}
                        >
                          {roleUserCounts[role]} Users
                        </div>
                        <div className="roles-table-container-header-icons">
                          <img
                            src={arrowWithBox}
                            alt="eye"
                            className="roles-table-container-header-icon"
                            onClick={() => handleRoleClick(role)}
                          />
                        </div>
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            {modules.map((mod, idx) => (
              <tbody className="role-row-container-tboday" key={idx}>
                <tr className="role-row-container">
                  <th className={"sticky-cl"}>
                    <div className="role-row-container-header">
                      <div>{mod.name}</div>
                      {expandedModules[mod.name] ? (
                        <img
                          src={icons.ExpandUp}
                          aria-hidden="true"
                          onClick={() => toggleModule(mod.name)}
                        />
                      ) : (
                        <img
                          src={icons.ExpandDown}
                          aria-hidden="true"
                          onClick={() => toggleModule(mod.name)}
                        />
                      )}
                    </div>
                  </th>
                  {roles.map((role) => (
                    <td className="permission-legend" key={role}>
                      <div className="permission-legend-text">
                        <div className="text-center">R</div>
                        <div className="text-center">W</div>
                        <div className="text-center">D</div>
                      </div>
                    </td>
                  ))}
                </tr>

                {expandedModules[mod.name] &&
                  mod.subresources.map((sub, subIdx) => (
                    <tr
                      key={`${idx}-${subIdx}`}
                      className="role-row-container-data"
                    >
                      <td className="role-row-container-data-text">{sub}</td>
                      {roles.map((role) => (
                        <td key={`${role}-${sub}`}>
                          <div className="role-permission-checks">
                            {["Import", "Export"].includes(sub)
                              ? (() => {
                                  const isChecked =
                                    (modifiedPermissions?.[role]?.[mod.name]?.[
                                      sub
                                    ]?.read &&
                                      modifiedPermissions?.[role]?.[mod.name]?.[
                                        sub
                                      ]?.write &&
                                      modifiedPermissions?.[role]?.[mod.name]?.[
                                        sub
                                      ]?.delete) ??
                                    (permissionsData?.[role]?.[mod.name]?.[sub]
                                      .read &&
                                      permissionsData?.[role]?.[mod.name]?.[sub]
                                        .write &&
                                      permissionsData?.[role]?.[mod.name]?.[sub]
                                        .delete) ??
                                    false;
                                  return (
                                    <div className="role-row-container-data-checkbox text-center">
                                      <input
                                        type="checkbox"
                                        checked={!!isChecked}
                                        disabled={
                                          !canWrite || role === "Super Admin"
                                        }
                                        onChange={() =>
                                          handleCheckboxChange(
                                            role,
                                            mod.name,
                                            sub
                                          )
                                        }
                                      />
                                    </div>
                                  );
                                })()
                              : ["read", "write", "delete"].map((scope) => {
                                  const isChecked =
                                    modifiedPermissions?.[role]?.[mod.name]?.[
                                      sub
                                    ]?.[scope as "read" | "write" | "delete"] ??
                                    permissionsData?.[role]?.[mod.name]?.[
                                      sub
                                    ]?.[scope as "read" | "write" | "delete"] ??
                                    false;

                                  return (
                                    <div
                                      key={`${role}-${sub}-${scope}`}
                                      className="role-row-container-data-checkbox text-center"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={!!isChecked}
                                        disabled={
                                          !canWrite ||
                                          role === "Super Admin" ||
                                          sub === "Assigned Leads"
                                        }
                                        onChange={() =>
                                          handleCheckboxChange(
                                            role,
                                            mod.name,
                                            sub,
                                            scope as "read" | "write" | "delete"
                                          )
                                        }
                                      />
                                    </div>
                                  );
                                })}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
              </tbody>
            ))}
          </Table>
        </div>
        <div className="roles-actions-container">
          {canWrite && (
            <>
              <button
                className="cancel-permission-button"
                type="button"
                onClick={() => {
                  if (createNewRole.isCreate) {
                    setCreateNewRole({ isCreate: false });
                    setPermissions(createNewRole?.oldPermissions);
                  }

                  setModifiedPermissions({});
                  const updated = permissions.map((item) => ({
                    ...item,
                    isShowData: false,
                  }));
                  setRolePermissions(updated);
                }}
              >
                Reset
              </button>
              <button
                className="save-permission-button"
                type="button"
                onClick={() => {
                  if (createNewRole.isCreate) {
                    handleCreateRole(createNewRole.newPermissions.role);
                  } else {
                    const result: any = applyScopeChangesToRoles(
                      permissions,
                      modifiedPermissions
                    );
                    const payload: any = {
                      userRolePermissions:
                        result.map((item: any) => {
                          const { userCount, ...rest } = item;
                          return rest;
                        }) || [],
                    };
                    handleUpdatePermissions(payload);
                  }
                }}
                disabled={Object.keys(modifiedPermissions).length === 0}
              >
                Save Changes
              </button>
            </>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="roles-container">
      {alertMessage.open && (
        <AlertComponent
          message={alertMessage.message}
          type={alertMessage.type || "info"}
          open={alertMessage.open}
          onClose={() => setAlertMessage({ open: false })}
        />
      )}
      <div>
        <BreadcrumbComponent breadcrumbLink={crumbMapping} />
      </div>
      <div className="roles-content-container">
        <div className="roles-header-container">
          <h1 className="roles-title">User Roles & Permissions</h1>
          <p className="roles-sub-title">
            Control access to every part of your system with clear roles and
            permissions. Assign, manage, and stay in control.
          </p>
          {isSuperParter && (
            <div className="roles-tab-container">
              <button
                className={`roles-tab-button ${activeTab === "AFP" ? "active" : ""}`}
                onClick={() => {
                  if (createNewRole?.isCreate) {
                    setCreateNewRole({ isCreate: false });
                  }
                  setActiveTab("AFP");
                  setModifiedPermissions({});
                  setCompany(null);
                }}
              >
                AFP Role & Permission
              </button>
              <button
                className={`roles-tab-button ${activeTab === "Partner" ? "active" : ""}`}
                onClick={() => {
                  if (createNewRole?.isCreate) {
                    setCreateNewRole({ isCreate: false });
                  }
                  setActiveTab("Partner");
                  setModifiedPermissions({});
                }}
              >
                Partner Role & Permission
              </button>
            </div>
          )}
          <div className="roles-header-actions">
            <div className="roles-header-actions-left">
              {!createNewRole?.isCreate && isSuperParter && canWrite && (
                <>
                  {" "}
                  <button
                    className={`roles-header-button ${((activeTab === "Partner") === (selectedCompany !== 1)) === (company === null) ? "" : "disabled"}`}
                    type="button"
                    onClick={() => setIsCreateRolePopupOpen(true)}
                    disabled={!company && activeTab === "Partner"}
                  >
                    Add New Role +
                  </button>
                </>
              )}
              <div>
                {activeTab === "Partner" && isSuperParter ? (
                  <CompanySelect
                    value={company}
                    onChange={(option) => {
                      setCompany(option);
                      if (createNewRole.isCreate) {
                        setCreateNewRole({ isCreate: false });
                        setPermissions(createNewRole?.oldPermissions);
                      }

                      setModifiedPermissions({});
                      const updated = permissions.map((item) => ({
                        ...item,
                        isShowData: false,
                      }));
                      setRolePermissions(updated);
                    }}
                    companyNames={companyNames}
                    disabled={false}
                  />
                ) : (
                  ""
                )}
              </div>
            </div>
            <div className="roles-header-button-text">
              <span>
                <b>R</b> - Read | <b>W</b> - Write | <b>D</b> - Delete
              </span>
              <span>Last updated {formatLastUpdatedDate(lastUpdated)}</span>
            </div>
          </div>
        </div>
        {!loading &&
          (activeTab === "AFP" ? (
            renderPermisssionComponent()
          ) : activeTab === "Partner" && company ? (
            renderPermisssionComponent()
          ) : (
            <div className="roles-partner-placeholder">
              <img src={backgroundIcon} />
              <p>
                To manage user roles and permissions, please start by selecting
                a company. Once a company is selected, you’ll be able to view,
                assign, and configure roles for its users.
              </p>
            </div>
          ))}
        {isRoleDetailsOpen && (
          <RoleDetails
            isOpen={isRoleDetailsOpen}
            onClose={() => setIsRoleDetailsOpen(false)}
            selectedCompany={selectedCompany}
            role={selectedRoleName}
          />
        )}
        {isPopupOpen && (
          <RolesConfirmationPopup
            isOpen={isPopupOpen}
            onClose={() => setIsPopupOpen(!isPopupOpen)}
            handleSaveChanges={handleSaveChanges}
          />
        )}
        {isCreateRolePopupOpen ? (
          <RolesCreationPopup
            isOpen={isCreateRolePopupOpen}
            onClose={() => setIsCreateRolePopupOpen(!isCreateRolePopupOpen)}
            handleSaveChanges={handleSaveChanges}
          />
        ) : (
          ""
        )}
      </div>
    </div>
  );
};

export default UserManagementRole;
